/**
 * 磁性套索的边缘吸附路径搜索（DESIGN.md 对比 Photoshop 补全）。
 *
 * 手感照真实 Photoshop：鼠标不用按住，移动到哪，路径就沿着附近的边缘
 * 吸附延伸到哪；点一下强制落一个"紧固点"；双击/回车闭合成选区（闭合
 * 逻辑、最终栅格化都复用既有的 `fromPolygon`/`lassoMask`，这个模块只
 * 管"两点之间怎么走一条贴边的路"）。
 *
 * 算法：亮度梯度强度（Sobel）当"边缘强度"；从上一个紧固点到当前指针
 * 位置，在两点外接矩形（按 `searchRadius` 扩一圈）限定的窗口内跑
 * Dijkstra 最短路径，边权是"常数 − 边缘强度"——边缘越强，走这条边代价
 * 越低，最短路径天然贴着边缘走，这是这类工具的标准实现思路。
 *
 * 简化（已在复盘记录披露）：
 * - 窗口面积有上限（`MAX_WINDOW_PIXELS`），指针一次移动太远（窗口会
 *   超限）时不搜索、直接退化成直线连接——不为了"完全贴合"去跑一次
 *   代价极高的搜索拖垮交互，真实 Photoshop 面对同样情形应该也有类似的
 *   性能兜底，只是没公开具体实现细节。
 * - 只用亮度梯度算边缘强度，不是真实 Photoshop 那种更复杂的边缘检测
 *   算法；"对比度"选项的效果简化成"梯度强度低于阈值就当没有边"，不是
 *   重新实现一套完整的边缘检测器。
 */
import type { SourceImage } from '../selection/magic-wand';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface MagneticLassoOptions {
  /** 搜索窗口半径（文档像素）——大致对应 PS 的"宽度"：数值越大，允许
   * 路径为了贴边而偏离两点连线越远，但窗口越大搜索越慢。 */
  readonly searchRadius: number;
  /** 0–1：梯度强度低于这个阈值的像素当"没有边"处理（代价拉满），
   * 大致对应 PS 的"对比度"——阈值越高，只有反差很大的边缘才会被贴上。 */
  readonly contrastThreshold: number;
}

/** 窗口总像素数上限（约 64×64）——超过就不搜索，退化直线连接。 */
const MAX_WINDOW_PIXELS = 4096;

function luminanceAt(pixels: Uint8Array, width: number, x: number, y: number): number {
  const at = (y * width + x) * 4;
  return 0.299 * pixels[at]! + 0.587 * pixels[at + 1]! + 0.114 * pixels[at + 2]!;
}

/** 单点 Sobel 梯度强度，0–1 归一化；越界坐标钳到图内（边界镜像的简化版）。 */
function gradientAt(image: SourceImage, x: number, y: number): number {
  const cx = (v: number): number => Math.min(image.width - 1, Math.max(0, v));
  const cy = (v: number): number => Math.min(image.height - 1, Math.max(0, v));
  const l = (dx: number, dy: number): number => luminanceAt(image.pixels, image.width, cx(x + dx), cy(y + dy));
  const gx = l(-1, -1) + 2 * l(-1, 0) + l(-1, 1) - (l(1, -1) + 2 * l(1, 0) + l(1, 1));
  const gy = l(-1, -1) + 2 * l(0, -1) + l(1, -1) - (l(-1, 1) + 2 * l(0, 1) + l(1, 1));
  return Math.min(1, Math.hypot(gx, gy) / (4 * 255));
}

/** 最小二叉堆（按 dist 排序），Dijkstra 专用，不做成通用数据结构。 */
class MinHeap {
  #idx: number[] = [];
  #dist: number[] = [];

  get size(): number {
    return this.#idx.length;
  }

  push(idx: number, dist: number): void {
    this.#idx.push(idx);
    this.#dist.push(dist);
    let i = this.#idx.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.#dist[parent]! <= this.#dist[i]!) break;
      this.#swap(parent, i);
      i = parent;
    }
  }

  pop(): number {
    const top = this.#idx[0]!;
    const lastIdx = this.#idx.pop()!;
    const lastDist = this.#dist.pop()!;
    if (this.#idx.length > 0) {
      this.#idx[0] = lastIdx;
      this.#dist[0] = lastDist;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = i * 2 + 2;
        let smallest = i;
        if (l < this.#idx.length && this.#dist[l]! < this.#dist[smallest]!) smallest = l;
        if (r < this.#idx.length && this.#dist[r]! < this.#dist[smallest]!) smallest = r;
        if (smallest === i) break;
        this.#swap(smallest, i);
        i = smallest;
      }
    }
    return top;
  }

  #swap(a: number, b: number): void {
    [this.#idx[a], this.#idx[b]] = [this.#idx[b]!, this.#idx[a]!];
    [this.#dist[a], this.#dist[b]] = [this.#dist[b]!, this.#dist[a]!];
  }
}

const NEIGHBORS: ReadonlyArray<readonly [number, number, number]> = [
  [-1, -1, Math.SQRT2],
  [0, -1, 1],
  [1, -1, Math.SQRT2],
  [-1, 0, 1],
  [1, 0, 1],
  [-1, 1, Math.SQRT2],
  [0, 1, 1],
  [1, 1, Math.SQRT2],
];

/**
 * 从 `from` 到 `to`（文档坐标）找一条贴着边缘走的路径，端点取整。
 * 找不到/窗口超限时原样返回 `[from, to]`（直线兜底）。
 */
export function traceMagneticPath(
  image: SourceImage,
  from: Point,
  to: Point,
  options: MagneticLassoOptions,
): Point[] {
  const straight = [from, to];
  const r = Math.max(1, Math.round(options.searchRadius));
  const x0 = Math.max(0, Math.floor(Math.min(from.x, to.x)) - r);
  const y0 = Math.max(0, Math.floor(Math.min(from.y, to.y)) - r);
  const x1 = Math.min(image.width - 1, Math.ceil(Math.max(from.x, to.x)) + r);
  const y1 = Math.min(image.height - 1, Math.ceil(Math.max(from.y, to.y)) + r);
  const w = x1 - x0 + 1;
  const h = y1 - y0 + 1;
  if (w <= 0 || h <= 0 || w * h > MAX_WINDOW_PIXELS) return straight;

  const fx = Math.round(from.x) - x0;
  const fy = Math.round(from.y) - y0;
  const tx = Math.round(to.x) - x0;
  const ty = Math.round(to.y) - y0;
  if (fx < 0 || fy < 0 || fx >= w || fy >= h || tx < 0 || ty < 0 || tx >= w || ty >= h) return straight;

  const n = w * h;
  const cost = new Float32Array(n);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const g = gradientAt(image, x0 + x, y0 + y);
      const strength = g < options.contrastThreshold ? 0 : g;
      cost[y * w + x] = 1 - strength;
    }
  }

  const dist = new Float64Array(n).fill(Infinity);
  const prev = new Int32Array(n).fill(-1);
  const visited = new Uint8Array(n);
  const start = fy * w + fx;
  const goal = ty * w + tx;
  dist[start] = 0;
  const heap = new MinHeap();
  heap.push(start, 0);

  while (heap.size > 0) {
    const u = heap.pop();
    if (visited[u] === 1) continue;
    visited[u] = 1;
    if (u === goal) break;
    const ux = u % w;
    const uy = (u - ux) / w;
    for (const [dx, dy, len] of NEIGHBORS) {
      const vx = ux + dx;
      const vy = uy + dy;
      if (vx < 0 || vy < 0 || vx >= w || vy >= h) continue;
      const v = vy * w + vx;
      if (visited[v] === 1) continue;
      const weight = ((cost[u]! + cost[v]!) / 2) * len;
      const nd = dist[u]! + weight;
      if (nd < dist[v]!) {
        dist[v] = nd;
        prev[v] = u;
        heap.push(v, nd);
      }
    }
  }

  if (!Number.isFinite(dist[goal]!)) return straight;

  const path: Point[] = [];
  let cur = goal;
  while (cur !== -1) {
    const cx = cur % w;
    const cy = (cur - cx) / w;
    path.push({ x: cx + x0, y: cy + y0 });
    cur = prev[cur]!;
  }
  path.reverse();
  return path;
}

/** 路径总长度（相邻点欧氏距离之和）——频率驱动的自动落点用它判断"够远了"。 */
export function pathLength(points: readonly Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
  }
  return total;
}
